/**
 * Audit Settings Tab
 *
 * Shown under Settings → Audit. Admin-only.
 * Lets admins toggle auditing on/off per category.
 * Every change is itself recorded in the audit log.
 */
import React, { useState } from 'react';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useAudit, type AuditCategoryConfig } from '@/contexts/AuditContext';

// Group the categories for display
const CATEGORY_GROUPS: { groupLabel: string; categories: string[] }[] = [
  {
    groupLabel: 'Scheduling',
    categories: ['Bookings'],
  },
  {
    groupLabel: 'Settings',
    categories: [
      'Settings – General',
      'Settings – Booking Types',
      'Settings – Timezones',
      'Settings – Group Setup',
      'Settings – Integrations',
      'Settings – Audit',
    ],
  },
  {
    groupLabel: 'Administration',
    categories: ['Roles & Permissions', 'Users'],
  },
];

type AppPage = 'overview' | 'calendar' | 'heatmap' | 'resources' | 'projects' | 'settings' | 'unallocated' | 'dependent-fields' | 'report-utilisation' | 'report-utilisation-forecast' | 'report-available-capacity' | 'report-resource-allocation' | 'report-project-staffing' | 'report-bench' | 'report-audit-log' | 'roles-permissions' | 'roles-permissions-edit' | 'user-setup';

export function AuditSettings({ onNavigate }: { onNavigate?: (page: AppPage) => void }) {
  const { categoryConfigs, updateCategoryConfig } = useAudit();
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);

  const getConfig = (category: string): AuditCategoryConfig | undefined =>
    categoryConfigs.find((c) => c.category === category);

  const handleToggle = (category: string, newEnabled: boolean) => {
    setPendingToggle(category);
    // Small async tick to let the UI render the optimistic state
    setTimeout(() => {
      updateCategoryConfig(category as AuditCategoryConfig['category'], newEnabled);
      setPendingToggle(null);
      toast.success(
        newEnabled ? 'Auditing enabled' : 'Auditing disabled',
        { description: `${category} auditing has been ${newEnabled ? 'enabled' : 'disabled'}.` },
      );
    }, 50);
  };

  const enabledCount = categoryConfigs.filter((c) => c.enabled).length;
  const totalCount = categoryConfigs.length;

  return (
    <div className="space-y-6">
      {/* Info card */}
      <Card className="border-primary/20">
        <CardContent className="pt-4">
          <Alert variant="info" size="compact">
            <AlertDescription className="text-xs">
              Audit records are <strong>immutable</strong> — they cannot be edited or deleted by
              any user. Disabling a category stops <em>new</em> entries from being captured but
              does not remove existing historical records.
            </AlertDescription>
          </Alert>

          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Currently auditing</span>
              <Badge variant="secondary" className="tabular-nums">
                {enabledCount} / {totalCount} categories
              </Badge>
            </div>
            {onNavigate && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1.5"
                onClick={() => onNavigate('report-audit-log')}
              >
                View Audit Log
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Category groups */}
      {CATEGORY_GROUPS.map((group, gi) => (
        <Card key={group.groupLabel} className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {group.groupLabel}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-0">
            {group.categories.map((cat, ci) => {
              const cfg = getConfig(cat);
              if (!cfg) return null;
              const isEnabled = cfg.enabled;
              const isPending = pendingToggle === cat;

              return (
                <React.Fragment key={cat}>
                  {ci > 0 && <Separator className="my-0" />}
                  <div className="flex items-start gap-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={`audit-toggle-${cat}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          {cfg.label}
                        </Label>
                        <Badge variant={isEnabled ? 'success' : 'secondary'} className="text-[10px] px-1.5 py-0">
                          {isEnabled ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{cfg.description}</p>
                    </div>
                    <Switch
                      id={`audit-toggle-${cat}`}
                      checked={isEnabled}
                      disabled={isPending}
                      onCheckedChange={(checked) => handleToggle(cat, checked)}
                      aria-label={`Toggle auditing for ${cfg.label}`}
                    />
                  </div>
                </React.Fragment>
              );
            })}
          </CardContent>
        </Card>
      ))}

      {/* Info footer */}
      <p className="text-xs text-muted-foreground pb-6">
        Audit settings changes are always recorded regardless of the &quot;Settings – Audit&quot; toggle state,
        to ensure administrator accountability.
      </p>
    </div>
  );
}
